import { HttpClient, HttpContext, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService, PROJECT_CONTEXT_TOKEN } from '@cosmos/angular/utils';
import { Measurement, SummaryResponse } from '@cosmos/shared';
import { catchError, map, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MeasurementsService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
  ) { }

  find(query=''): Observable<HttpResponse<Measurement[] | number>> {
    return this.http
      .get<any>(`${environment.API_URL}/measurements${query}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  findById(id: string, query = ''): Observable<HttpResponse<Measurement>> {
    return this.http
      .get<Measurement>(`${environment.API_URL}/measurements/${id}${query}`, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  create(body: Measurement, projectId?: string) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post(`${environment.API_URL}/measurements`, body, {observe: 'response', context})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  update(body: any, projectId?: string): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .patch<Measurement>(`${environment.API_URL}/measurements/${body[0]}`, body[1], {observe: 'response', context})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  delete(id: string) {
    return this.http
      .delete(`${environment.API_URL}/measurements/${id}`, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  finish(id: string, projectId?: string): Observable<Measurement> {
    const context = new HttpContext();
    if (projectId) context.set(PROJECT_CONTEXT_TOKEN, projectId);
    return this.http
    .post<Measurement>(`${environment.API_URL}/measurements/finish/${id}`, {}, { context })
      .pipe(
        map(response => response),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  changeDevice(id:string, body: any, projectId?: string){
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/changedevice/${id}`, body, {observe:'response', context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  changeStage(id: string, body: { stage: string }, projectId?: string){
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/movestage/${id}`, body, {observe:'response', context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  changeConcrete(id: string, body: { concrete: string; specifications?: number[]; specifications_maturity?: number[]; specifications_maturity_relative?: number[] }, projectId?: string){
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/changeconcrete/${id}`, body, {observe:'response', context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  downloadCSV(query: string) {
    return this.http
      .get(`${environment.API_URL}/measurements/download?${query}`, {responseType :'blob', observe: "response"})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  getPredictions(id: string, projectId?: string): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .get<Measurement>(`${environment.API_URL}/measurements/predictions/${id}`, {observe: 'response', context})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  downloadReport(id: string) {
    return this.http
      .get(`${environment.API_URL}/measurements/report/${id}`, {responseType :'blob', observe: "response"})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  generateReport(id: string) {
    return this.http
      .post(`${environment.API_URL}/measurements/report/${id}`, {}, {responseType :'blob', observe: "response"})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  reschedule(id: string, timestampStart: number, timestampEnd: number, projectId?: string): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/reschedule/${id}/${timestampStart}/${timestampEnd}`, {}, {observe: "response", context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  start(id: string, timestampStart: number, projectId?: string): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/start/${id}/${timestampStart}`, {}, {observe: "response", context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  unstart(id: string, projectId?: string): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/unstart/${id}`, {}, {observe: "response", context})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        )
  }

  removeDatapoint(
    id: string,
    body: { channelKey: string; ts: string; reason?: string },
    projectId?: string,
  ): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/${id}/datapoints/remove`, body, { observe: 'response', context })
      .pipe(
        map(response => response),
        catchError((error) => { this.errorService.handleError(error); throw error; }),
      );
  }

  restoreDatapoint(
    id: string,
    body: { channelKey: string; ts: string },
    projectId?: string,
  ): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/${id}/datapoints/restore`, body, { observe: 'response', context })
      .pipe(
        map(response => response),
        catchError((error) => { this.errorService.handleError(error); throw error; }),
      );
  }

  rescheduleWindow(
    id: string,
    body: { startTs: string | null; endTs: string | null },
    projectId?: string,
  ): Observable<HttpResponse<Measurement>> {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post<Measurement>(`${environment.API_URL}/measurements/${id}/reschedule-window`, body, { observe: 'response', context })
      .pipe(
        map(response => response),
        catchError((error) => { this.errorService.handleError(error); throw error; }),
      );
  }

  getSummary(measurementIds: string[], config: object): Observable<HttpResponse<SummaryResponse>> {
    return this.http
      .post<SummaryResponse>(
        `${environment.API_URL}/measurements/summary?_id=${measurementIds.join(',')}`,
        { config },
        { observe: 'response' },
      )
      .pipe(
        map(response => response),
        catchError((error) => { this.errorService.handleError(error); throw error; }),
      );
  }

  downloadSummaryXlsx(measurementIds: string[], body: object): Observable<HttpResponse<Blob>> {
    return this.http
      .post(
        `${environment.API_URL}/measurements/summary/xlsx?_id=${measurementIds.join(',')}`,
        body,
        { responseType: 'blob', observe: 'response' },
      )
      .pipe(
        map(response => response as HttpResponse<Blob>),
        catchError((error) => { this.errorService.handleError(error); throw error; }),
      );
  }
  // TODO: evaluate purpose of this method. Was implemented by gemini but never used
  // getProjectSHMSummary(projectId: string): Observable<HttpResponse<any>> {
  //   return this.http
  //     .get<any>(`${environment.API_URL}/measurements/shm-summary/${projectId}`, {observe: 'response'})
  //     .pipe(
  //       map(response => {return response;}),
  //       catchError( (error) =>{this.errorService.handleError(error); throw error;})
  //     );
  // }

  exploreMeasurements(projectId: string, params: any): Observable<HttpResponse<any[]>> {
    return this.http
      .post<any[]>(`${environment.API_URL}/measurements/aggregation/data-explorer/${projectId}`, params, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  /** Superadmin only: same query as `exploreMeasurements`, plus diagnostics about how it was applied. */
  exploreMeasurementsDebug(projectId: string, params: any): Observable<HttpResponse<{ datasets: any[]; diagnostics: any[] }>> {
    return this.http
      .post<{ datasets: any[]; diagnostics: any[] }>(`${environment.API_URL}/measurements/aggregation/data-explorer/${projectId}/debug`, params, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getAvailableMeasurementTypes(projectId: string): Observable<HttpResponse<string[]>> {
    return this.http
      .get<string[]>(`${environment.API_URL}/measurements/aggregation/types/${projectId}`, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  getDistinctValues(projectId: string, field: string, measurementType?: string): Observable<HttpResponse<any[]>> {
    let url = `${environment.API_URL}/measurements/aggregation/distinct/${projectId}/${field}`;
    if (measurementType) {
      url += `?measurementType=${measurementType}`;
    }
    return this.http
      .get<any[]>(url, {observe: 'response'})
      .pipe(
        map(response => {return response;}),
        catchError( (error) =>{this.errorService.handleError(error); throw error;})
      );
  }
}
